import { useState } from 'react';
import { Star, CreditCard, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import {
  appsBillingApiSubscriptionsCreateSubscriptionCheckout,
  appsBillingApiSubscriptionsGetPortalUrl,
} from '@/lib/api';
import { PremiumBadge } from '@/components/ui/premium-badge';
import { isStripeEnabled } from '@/lib/features';

interface MembershipSectionProps {
  isOwnProfile: boolean;
}

/**
 * MembershipSection displays subscription status and actions for the user's profile.
 * - For non-premium users: Shows "Upgrade to Premium" button
 * - For premium users: Shows subscription status and "Manage Membership" link
 */
const MembershipSection = ({ isOwnProfile }: MembershipSectionProps) => {
  const { isPremium, subscriptionStatus } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  // Only show this section on the user's own profile when Stripe is enabled
  if (!isOwnProfile || !isStripeEnabled()) {
    return null;
  }

  const handleUpgrade = async () => {
    setIsLoading(true);
    try {
      const currentUrl = window.location.href;
      const response = await appsBillingApiSubscriptionsCreateSubscriptionCheckout({
        success_url: `${window.location.origin}/profile?subscription=success`,
        cancel_url: currentUrl,
      });

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to start checkout. Please try again.',
        variant: 'destructive',
      });
      setIsLoading(false);
    }
  };

  const handleManageMembership = async () => {
    setIsLoading(true);
    try {
      const response = await appsBillingApiSubscriptionsGetPortalUrl({
        return_url: window.location.href,
      });

      // Redirect to Stripe Customer Portal
      window.location.href = response.data.portal_url;
    } catch (error: any) {
      console.error('Error getting portal URL:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to open billing portal. Please try again.',
        variant: 'destructive',
      });
      setIsLoading(false);
    }
  };

  const getStatusDisplay = () => {
    switch (subscriptionStatus) {
      case 'active':
        return { text: 'Active', className: 'text-green-400' };
      case 'past_due':
        return { text: 'Past Due', className: 'text-yellow-400' };
      case 'canceled':
        return { text: 'Canceled', className: 'text-red-400' };
      case 'unpaid':
        return { text: 'Unpaid', className: 'text-red-400' };
      default:
        return { text: 'None', className: 'text-adv-lightGray' };
    }
  };

  const statusDisplay = getStatusDisplay();

  return (
    <div className="glass-card rounded-lg p-6">
      <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
        <Star className="h-5 w-5 text-adv-orange" />
        Membership
      </h2>

      {isPremium ? (
        // Premium user view
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-3">
            <PremiumBadge size="md" showTooltip={false} />
            <span className="font-medium">Premium Member</span>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Status</span>
              <span className={statusDisplay.className}>{statusDisplay.text}</span>
            </div>
          </div>

          <div className="pt-2">
            <Button
              onClick={handleManageMembership}
              disabled={isLoading}
              variant="outline"
              className="w-full"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Manage Membership
                  <ExternalLink className="w-3 h-3 ml-2" />
                </>
              )}
            </Button>
            <p className="text-xs text-adv-lightGray mt-2 text-center">
              Update payment method, view billing history, or cancel
            </p>
          </div>
        </div>
      ) : (
        // Non-premium user view
        <div className="space-y-4">
          <p className="text-sm text-adv-lightGray">
            Unlock premium features like GPX track downloads and more.
          </p>

          <ul className="text-sm space-y-2">
            <li className="flex items-center gap-2">
              <Star className="h-3.5 w-3.5 text-adv-orange fill-current" />
              <span>Download GPX tracks from rides</span>
            </li>
            <li className="flex items-center gap-2">
              <Star className="h-3.5 w-3.5 text-adv-orange fill-current" />
              <span>Share your GPX tracks with others</span>
            </li>
            <li className="flex items-center gap-2">
              <Star className="h-3.5 w-3.5 text-adv-orange fill-current" />
              <span>Premium badge on your profile</span>
            </li>
            <li className="flex items-center gap-2">
              <Star className="h-3.5 w-3.5 text-adv-orange fill-current" />
              <span>Discounts on rally tickets</span>
            </li>
          </ul>

          <Button
            onClick={handleUpgrade}
            disabled={isLoading}
            className="w-full bg-adv-orange hover:bg-adv-orange/90"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <Star className="w-4 h-4 mr-2" />
                Upgrade to Premium
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default MembershipSection;
